import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SuporteIcon } from '@/components/icons/CustomIcons';
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useFeedback } from '@/components/ui/feedback';
import { supabase } from '@/lib/supabase';
import { Loader2, AlertCircleIcon, PaperclipIcon, UploadIcon, XIcon } from 'lucide-react';
import { useFileUpload, formatBytes } from '@/hooks/use-file-upload';

const SUPPORT_CATEGORIES = {
  "Acesso e Autenticação": [
    "Erro ao fazer login",
    "Esqueci minha senha",
    "Usuário sem permissão",
    "Problemas com autenticação 2FA",
    "Conta bloqueada"
  ],
  "Erros no Sistema": [
    "Erro inesperado (mensagem de erro)",
    "Página não carrega",
    "Função não executa como esperado",
    "Loop ou travamento no sistema",
    "Campos não carregam ou aparecem em branco"
  ],
  "Desempenho": [
    "Sistema lento",
    "Lentidão ao carregar dados",
    "Demora para salvar informações",
    "Tarefas demorando para processar",
    "Alto consumo de memória (client-side)"
  ],
  "Integrações e Automação": [
    "Integração com API falhando",
    "Webhook não disparou",
    "Automação não funcionou",
    "Integração com WhatsApp (Evolution API / n8n) falhando",
    "Dados não sincronizam entre sistemas"
  ],
  "Banco de Dados e Dados": [
    "Registro não encontrado",
    "Dados inconsistentes",
    "Erro ao salvar informações",
    "Duplicidade de dados",
    "Importação/Exportação não funciona"
  ],
  "Interface e Navegação": [
    "Problema com layout",
    "Botões não respondem",
    "Modal não abre / fecha",
    "Ícones não aparecem",
    "Navegação confusa ou indisponível"
  ],
  "Relatórios e Dashboards": [
    "Painel não carrega",
    "Métricas incorretas",
    "Gráficos quebrados",
    "Filtros não funcionam",
    "Arquivos não exportam (PDF, Excel, etc.)"
  ],
  "Notificações e Comunicação": [
    "Não recebi notificação",
    "Notificações duplicadas",
    "WhatsApp não enviou",
    "E-mails não dispararam",
    "Alertas incorretos"
  ],
  "Pagamentos e Financeiro": [
    "Fatura não gerada",
    "Erro no processamento do pagamento",
    "Dados financeiros divergentes",
    "Integração com plataforma de pagamento"
  ],
  "Cadastros": [
    "Não consigo cadastrar",
    "Não consigo editar",
    "Não consigo excluir",
    "Vínculos incorretos entre entidades"
  ],
  "Permissões e Perfis": [
    "Usuário sem acesso",
    "Perfil incorreto",
    "Função bloqueada indevidamente",
    "Problema ao alterar permissões"
  ],
  "Arquivos e Mídias": [
    "Upload não funciona",
    "Download não funciona",
    "Arquivo corrompido",
    "Pré-visualização falhando"
  ],
  "Mobile / Responsividade": [
    "Problemas no celular",
    "Layout quebrado no tablet",
    "Funções indisponíveis no mobile"
  ],
  "Configurações Gerais": [
    "Não consigo alterar configurações",
    "Preferências não salvam",
    "Customizações não refletem no sistema"
  ],
  "Outros": [
    "Suporte geral",
    "Dúvidas sobre uso",
    "Solicitação de melhoria",
    "Solicitação de nova funcionalidade"
  ]
};

type Priority = 'Baixa' | 'Média' | 'Alta';

export function SupportDialog() {
  const { user } = useAuth();
  const { showFeedback } = useFeedback();
  const [isOpen, setIsOpen] = useState(false);

  // Form States
  const [category, setCategory] = useState<string>('');
  const [subcategory, setSubcategory] = useState<string>('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<Priority>('Baixa');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const maxSize = 10 * 1024 * 1024; // 10MB default

  const [
    { files, isDragging, errors },
    {
      handleDragEnter,
      handleDragLeave,
      handleDragOver,
      handleDrop,
      openFileDialog,
      removeFile,
      getInputProps,
    },
  ] = useFileUpload({
    maxSize,
    initialFiles: [],
  });

  const file = files[0];

  // Reset subcategory when category changes
  useEffect(() => {
    setSubcategory('');
  }, [category]);

  const uploadFile = async (fileObj: File): Promise<string | null> => {
    try {
      const fileExt = fileObj.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${user?.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('support-files')
        .upload(filePath, fileObj);

      if (uploadError) {
        throw uploadError;
      }

      const { data } = supabase.storage.from('support-files').getPublicUrl(filePath);
      return data.publicUrl;
    } catch (error) {
      console.error('Erro ao fazer upload do arquivo:', error);
      showFeedback('error', 'Erro ao anexar arquivo. Tente novamente.');
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let fileUrl = null;
      if (file && file.file) {
        fileUrl = await uploadFile(file.file);
        if (!fileUrl) {
          // Se falhou o upload, interrompe (já mostrou erro no uploadFile)
          setIsSubmitting(false);
          return;
        }
      }

      const supportData = {
        user: {
          name: user?.name || 'Usuário Desconhecido',
          email: user?.email || 'Sem email',
          id: user?.id || 'anon'
        },
        category,
        subcategory,
        description,
        priority,
        fileUrl
      };

      const { error } = await supabase.functions.invoke('create-support-ticket', {
        body: supportData
      });

      if (error) throw error;

      // Reset form
      setCategory('');
      setSubcategory('');
      setDescription('');
      setPriority('Baixa');
      if (file) removeFile(file.id);
      setIsOpen(false);

      showFeedback('success', 'Sua solicitação de suporte foi enviada com sucesso! Em breve entraremos em contato.');
    } catch (error: any) {
      console.error('Erro ao enviar solicitação:', error);

      if (error && typeof error === 'object' && error.context && typeof error.context.json === 'function') {
        error.context.json().then((body: any) => {
          console.error('FULL ERROR BODY:', JSON.stringify(body, null, 2));

          // FORCE ALERT EVERYTHING
          alert(`ERRO DETALHADO (Copie tudo abaixo):\n\n${JSON.stringify(body, null, 2)}`);

          showFeedback('error', `Erro: ${body.error || 'Falha na solicitação'}`);
        }).catch((e: any) => {
          console.error('Error parsing JSON:', e);
          alert('Erro ao ler resposta do servidor. Verifique o console.');
        });
      } else {
        alert('Erro desconhecido. Verifique o console.');
        showFeedback('error', 'Erro ao enviar solicitação.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const subcategories = category ? (SUPPORT_CATEGORIES as any)[category] || [] : [];

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <SuporteIcon className="h-4 w-4" size={16} />
          Suporte
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
        <div className="p-6 pb-4">
          <DialogHeader>
            <DialogTitle>Solicitação de Suporte</DialogTitle>
            <DialogDescription>
              Preencha os campos abaixo para relatar um problema ou solicitar ajuda.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="flex-1 overflow-y-auto p-6 pt-0">
          <form id="support-form" onSubmit={handleSubmit} className="space-y-4">

            {/* Usuário */}
            <div className="space-y-2">
              <Label htmlFor="user">Usuário</Label>
              <Input
                id="user"
                value={user ? `${user.name} (${user.email})` : 'Usuário não identificado'}
                disabled
                className="bg-muted"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Categoria */}
              <div className="space-y-2">
                <Label htmlFor="category">Categoria do Problema</Label>
                <Select value={category} onValueChange={setCategory} required>
                  <SelectTrigger id="category">
                    <SelectValue placeholder="Selecione uma categoria" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {Object.keys(SUPPORT_CATEGORIES).map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Subcategoria */}
              <div className="space-y-2">
                <Label htmlFor="subcategory">Detalhe do Problema</Label>
                <Select
                  value={subcategory}
                  onValueChange={setSubcategory}
                  disabled={!category}
                  required
                >
                  <SelectTrigger id="subcategory">
                    <SelectValue placeholder="Selecione o detalhe" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {subcategories.map((sub: string) => (
                      <SelectItem key={sub} value={sub}>
                        {sub}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Prioridade */}
            <div className="space-y-2">
              <Label htmlFor="priority">Prioridade</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as Priority)} required>
                <SelectTrigger id="priority">
                  <SelectValue placeholder="Selecione a prioridade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Baixa">Baixa</SelectItem>
                  <SelectItem value="Média">Média</SelectItem>
                  <SelectItem value="Alta">Alta</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Descrição */}
            <div className="space-y-2">
              <Label htmlFor="description">Descrição do Problema</Label>
              <Textarea
                id="description"
                placeholder="Descreva detalhadamente o que está acontecendo..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="min-h-[100px] resize-none"
                required
              />
            </div>

            {/* Anexar Arquivo */}
            <div className="flex flex-col gap-2">
              <Label>Anexar Arquivo (Opcional)</Label>

              {/* Drop area */}
              <div
                role="button"
                onClick={openFileDialog}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                data-dragging={isDragging || undefined}
                className="flex min-h-24 flex-col items-center justify-center rounded-xl border border-dashed border-input p-2 transition-colors hover:bg-accent/50 has-disabled:pointer-events-none has-disabled:opacity-50 has-[input:focus]:border-ring has-[input:focus]:ring-[3px] has-[input:focus]:ring-ring/50 data-[dragging=true]:bg-accent/50"
              >
                <input
                  {...getInputProps()}
                  className="sr-only"
                  aria-label="Upload file"
                  disabled={Boolean(file)}
                />

                <div className="flex flex-col items-center justify-center text-center">
                  <div
                    className="mb-2 flex size-11 shrink-0 items-center justify-center rounded-full border bg-background"
                    aria-hidden="true"
                  >
                    <UploadIcon className="size-4 opacity-60" />
                  </div>
                  <p className="mb-1.5 text-sm font-medium">Anexar arquivo</p>
                  <p className="text-xs text-muted-foreground">
                    Arraste e solte ou clique para navegar (max. {formatBytes(maxSize)})
                  </p>
                </div>
              </div>

              {errors.length > 0 && (
                <div
                  className="flex items-center gap-1 text-xs text-destructive"
                  role="alert"
                >
                  <AlertCircleIcon className="size-3 shrink-0" />
                  <span>{errors[0]}</span>
                </div>
              )}

              {/* File list */}
              {file && (
                <div className="space-y-2">
                  <div
                    key={file.id}
                    className="flex items-center justify-between gap-2 rounded-xl border px-4 py-2"
                  >
                    <div className="flex items-center gap-3 overflow-hidden">
                      <PaperclipIcon
                        className="size-4 shrink-0 opacity-60"
                        aria-hidden="true"
                      />
                      <div className="min-w-0">
                        <p className="truncate text-[13px] font-medium">
                          {file.name}
                        </p>
                      </div>
                    </div>

                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="-me-2 size-8 text-muted-foreground/80 hover:bg-transparent hover:text-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(file.id);
                      }}
                      aria-label="Remove file"
                    >
                      <XIcon className="size-4" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:gap-3 sm:justify-end sm:items-center p-4 bg-muted/40 border-t flex-shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsOpen(false)}
            className="flex-1 sm:flex-none"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            form="support-form"
            variant="success"
            disabled={!category || !subcategory || !description.trim() || isSubmitting}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Enviando...
              </>
            ) : (
              'Enviar Solicitação'
            )}
          </Button>
        </div>
      </DialogContent >
    </Dialog >
  );
}
